<template>
  <div class="kpt-remote-map">
    <div id="mapContainer" class="kpt-remote-map-container"></div>
    <div class="kpt-remote-map-meta">
      <div class="kpt-remote-map-zones">
        <b-table
          :data="zones"
          :columns="zonesColumns"
          :selected.sync="selectedZone"
          hoverable
          @select="zoomToZone">
        </b-table>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import mapboxgl from 'mapbox-gl';
import * as MapboxDraw from '@mapbox/mapbox-gl-draw';
import uuidv1 from 'uuid/v1';
import { reverseLatLong } from '../../shared/mixins/reverseLatLong';
import ZoneDetail from '../../zones/components/ZoneDetailComponent.vue';

const ZoneDetailPopupClass = Vue.extend(ZoneDetail);

export default {
  data() {
    return {
      map: null,
      draw: null,
      selectedFeature: null,
      zonesColumns: [{ field: 'name', label: 'Zones', narrowed: true }],
      selectedZone: null,
      popup: null
    }
  },
  name: 'RemoteMap',
  props: {
    selectedRemotes: {
      type: Array,
      default: () => []
    },
    remotes: {
      type: Array,
      default: () => []
    }
  },
  mixins: [reverseLatLong],
  created() {
    mapboxgl.accessToken = this.$store.state.appConfig.mapboxKey;
  },
  mounted() {
    if (this.map == null) {
      // store 'this', because mapbox is bad
      const self = this;
      setTimeout(async () => {
        // instantiate the map
        this.map = new mapboxgl.Map({
          container: 'mapContainer',
          style: 'mapbox://styles/mapbox/light-v9',
          center: [0, 40], // offset a bit to show more civilization
          zoom: 2
        });
        // instantiate the draw plugin for the map
        this.draw = new MapboxDraw({ controls: { polygon: true, trash: true }, displayControlsDefault: false });
        // add draw controls
        this.map.addControl(this.draw, 'top-left');
        // bind event handlers for draw actions
        this.map.on('draw.create', this.zoneCreated);
        this.map.on('draw.update', this.zoneUpdated);
        this.map.on('draw.selectionchange', this.zoneSelected);
        // when map loads, draw the zones
        this.map.on('load', () => {
          if (self.featureCollection) self.draw.set(self.featureCollection);
        });
      }, 50);
    }
  },
  watch: {
    // eslint-disable-next-line quote-props, no-unused-vars, func-names
    'zones': function (newValue, oldValue) {
      if (newValue.length) {
        this.kyZones = newValue;
      }
    }
  },
  methods: {
    async zoneCreated(e) {
      // clone the old id for deletion
      const oldId = `${e.features[0].id}`;
      // make a new id
      const newId = uuidv1();
      // replace the id on the created object
      e.features[0].id = newId;
      // create a new zone
      this.draw.add(e.features[0]);
      // delete the old object we just created
      this.draw.delete(oldId);
      // get all the zones, we just need it for the count
      const existingZones = this.draw.getAll();
      // create a new zone to add on the server
      const zoneToAdd = {
        id: e.features[0].id,
        name: `Zone #${existingZones.features.length}`,
        coordinates: this.reverseLatLong(e.features[0].geometry.coordinates[0])
      }
      // add the zone to the database
      await this.$store.dispatch('addZone', zoneToAdd);
    },
    async zoneSelected(e) {
      if (e.features[0]) {
        // fetch the zone from the api
        await this.$store.dispatch('fetchZone', e.features[0].id);
        // get & reverse the coordinates
        const coordinates = this.reverseLatLong(e.features[0].geometry.coordinates);
        // get the coordinates where to actually display the popup
        const coordsToDisplay = coordinates.slice()[0][0];
        // create the popup instance
        this.popup = new mapboxgl.Popup({ closeButton: false, className: 'kpt-zone-popup', maxWidth: 'none' })
          .setLngLat(coordsToDisplay)
          .setHTML('<div id="zone-popup-content"></div>')
          .addTo(this.map);
        // https://tech.beyondtracks.com/posts/mapbox-gl-popups-with-vue/
        // create a new instance of the zone detail component
        const popupInstance = new ZoneDetailPopupClass({
          propsData: {
            zone: this.zone,
            loading: this.zoneLoading
          }
        });
        // mount that component on the created popup
        popupInstance.$mount('#zone-popup-content')
        // force update of the popup to refresh the content
        // eslint-disable-next-line no-underscore-dangle
        this.popup._update();
      }
    },
    async zoneUpdated(e) {
      if (e.features[0]) {
        // console.log(e.features[0]);
      }
    },
    async zoomToZone(zone) {
      const coords = zone.coordinates;
      const bounds = coords.reduce((bound, coord) => bounds.extend(coord), new mapboxgl.LngLatBounds(coords[0], coords[0]));
      await this.$store.dispatch('fetchZone', zone.id);
      this.map.fitBounds(bounds, {
        padding: 50
      });
    }
  },
  computed: {
    zones() {
      return this.$store.state.zones.zones;
    },
    zone() {
      return this.$store.state.zones.zone;
    },
    zoneLoading() {
      return this.$store.state.zones.zoneLoading;
    },
    featureCollection() {
      return this.$store.getters.reversedZones;
    }
  }
}
</script>
<style lang="scss" scoped>
@import "../../../../node_modules/mapbox-gl/dist/mapbox-gl.css";
@import '../../../../node_modules/@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
@import "../../styles/_colors";

.kpt-remote-map {
  position: relative;
  height: 50%;
  max-height: 50%;
  border-top: 1px solid $grey;
  border-bottom: 1px solid $grey;

  .kpt-remote-map-container {
    height: 100%;
    width: 100%;
  }

  .kpt-remote-map-meta {
    position: absolute;
    right: 15px;
    top: 15px;
    height: calc(100% - 30px);
    max-height: calc(100% - 30px);
    display: flex;
    justify-content: flex-end;
    z-index: 1;

    .kpt-remote-map-zones {
      background: $white;
      overflow-y: auto;
      min-width: 250px;
      margin-left: 15px;
    }
  }
}
</style>
